import { Button, Checkbox, Dropdown, Table, theme } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  MoreOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { CustomerLocationResponse } from '@/types/api/customer';
import TableContainer from '@/components/common/TableContainer';
import { controlHeightTokens } from '@/styles/tokens';
import { LocationAddressDisplay } from '@/components/common/LocationAddressDisplay';
import ExpandableText from '@/components/common/ExpandableText';
import { getToggleStatusStyle } from '@/hooks/useCustomerPermissions';

interface LocationsSectionProps {
  locations: CustomerLocationResponse[] | undefined;
  includeInactive: boolean;
  onIncludeInactiveChange: (checked: boolean) => void;
  onAddLocation: () => void;
  onEditLocation: (location: CustomerLocationResponse) => void;
  onViewLocation: (location: CustomerLocationResponse) => void;
  onSetDefaultShipping: (location: CustomerLocationResponse) => void;
  onSetDefaultBilling: (location: CustomerLocationResponse) => void;
  onToggleStatus: (location: CustomerLocationResponse) => void;
  loading: boolean;
  canAdd: boolean;
  getItemPermissions: (item: CustomerLocationResponse) => {
    canEdit: boolean;
    canSetPrimary: boolean;
    canSetDefault: boolean;
    canSetDefaultShipping: boolean;
    canSetDefaultBilling: boolean;
    canToggleStatus: boolean;
    canDelete: boolean;
    isItemActive: boolean;
    isProtectedFromDeactivate: boolean;
  };
}

const LocationsSection = ({
  locations,
  includeInactive,
  onIncludeInactiveChange,
  onAddLocation,
  onEditLocation,
  onViewLocation,
  onSetDefaultShipping,
  onSetDefaultBilling,
  onToggleStatus,
  loading,
  canAdd,
  getItemPermissions,
}: LocationsSectionProps) => {
  const { t } = useTranslation('customers');
  const { token } = theme.useToken();

  return (
    <div>
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <h3
          style={{
            fontSize: token.fontSizeLG,
            fontWeight: token.fontWeightStrong,
            margin: 0,
          }}
        >
          {t('customer.sections.locations')}
        </h3>
        <div className="tw-flex tw-items-center tw-gap-4">
          <Checkbox
            checked={includeInactive}
            onChange={(e) => onIncludeInactiveChange(e.target.checked)}
          >
            {t('customer.labels.includeInactiveLocations')}
          </Checkbox>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!canAdd}
            style={{
              height: `${controlHeightTokens.controlHeightXL}px`,
            }}
            onClick={onAddLocation}
          >
            {t('customer.modals.add')}
          </Button>
        </div>
      </div>
      <TableContainer>
        <Table<CustomerLocationResponse>
          dataSource={
            locations
              ?.filter((l) => includeInactive || l.isActive)
              ?.slice()
              ?.sort((a, b) => {
                // Default shipping first
                if (a.isDefaultShipping !== b.isDefaultShipping) {
                  return a.isDefaultShipping ? -1 : 1;
                }
                // Then default billing
                if (a.isDefaultBilling !== b.isDefaultBilling) {
                  return a.isDefaultBilling ? -1 : 1;
                }
                // Then by locationId (newer items have higher IDs)
                return b.locationId - a.locationId;
              }) || []
          }
          rowKey="locationId"
          rowClassName={(record) => !record.isActive ? 'inactive-row' : ''}
          onRow={(record) => ({
            onClick: () => onViewLocation(record),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            pageSize: 10,
          }}
          scroll={{ x: 'max-content' }}
          loading={loading}
          columns={[
            {
              title: t('customer.fields.defaultShipping'),
              dataIndex: 'isDefaultShipping',
              key: 'isDefaultShipping',
              width: 160,
              align: 'center',
              showSorterTooltip: false,
              render: (isDefaultShipping: boolean) =>
                isDefaultShipping ? (
                  <CheckOutlined
                    style={{ color: token.colorPrimary, fontSize: token.sizeMD }}
                  />
                ) : null,
            },
            {
              title: t('customer.fields.defaultBilling'),
              dataIndex: 'isDefaultBilling',
              key: 'isDefaultBilling',
              width: 160,
              align: 'center',
              showSorterTooltip: false,
              render: (isDefaultBilling: boolean) =>
                isDefaultBilling ? (
                  <CheckOutlined
                    style={{ color: token.colorPrimary, fontSize: token.sizeMD }}
                  />
                ) : null,
            },
            {
              title: t('customer.fields.locationName'),
              dataIndex: 'locationName',
              key: 'locationName',
              width: 180,
              sorter: (a, b) => a.locationName.localeCompare(b.locationName),
              showSorterTooltip: false,
            },
            {
              title: t('customer.fields.address'),
              key: 'address',
              width: 300,
              ellipsis: true,
              sorter: (a, b) => {
                // Build full address for sorting
                const getFullAddress = (record: CustomerLocationResponse) => {
                  const parts = [];
                  if (record.address) parts.push(record.address);
                  if (record.city) parts.push(record.city);
                  if (record.state) parts.push(record.state);
                  if (record.postcode) parts.push(record.postcode);
                  if (record.country) parts.push(record.country);
                  return parts.join(', ');
                };
                return getFullAddress(a).localeCompare(getFullAddress(b));
              },
              showSorterTooltip: false,
              render: (_: unknown, record: CustomerLocationResponse) => (
                <LocationAddressDisplay location={record} />
              ),
            },
            {
              title: t('customer.fields.notes'),
              dataIndex: 'notes',
              key: 'notes',
              width: 200,
              render: (notes: string | null) => (
                <ExpandableText text={notes} maxLength={50} />
              ),
            },
            {
              title: t('customer.fields.actions'),
              key: 'actions',
              width: 80,
              fixed: 'right',
              onCell: () => ({ onClick: (e: React.MouseEvent) => e.stopPropagation() }),
              render: (_: unknown, record: CustomerLocationResponse) => {
                const itemPerms = getItemPermissions(record);
                return (
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'setDefaultShipping',
                          label: t('customer.actions.setAsDefaultShipping'),
                          icon: <CheckOutlined />,
                          disabled: !itemPerms.canSetDefaultShipping,
                          onClick: () => onSetDefaultShipping(record),
                        },
                        {
                          key: 'setDefaultBilling',
                          label: t('customer.actions.setAsDefaultBilling'),
                          icon: <CheckOutlined />,
                          disabled: !itemPerms.canSetDefaultBilling,
                          onClick: () => onSetDefaultBilling(record),
                        },
                        {
                          key: 'edit',
                          label: t('customer.actions.edit'),
                          icon: <EditOutlined />,
                          disabled: !itemPerms.canEdit,
                          onClick: () => onEditLocation(record),
                        },
                        {
                          type: 'divider',
                        },
                        {
                          key: 'toggleStatus',
                          label: itemPerms.isItemActive ? t('customer.actions.deactivate') : t('customer.actions.activate'),
                          icon: itemPerms.isItemActive ? <CloseCircleOutlined /> : <CheckCircleOutlined />,
                          ...getToggleStatusStyle(itemPerms, token),
                          onClick: () => onToggleStatus(record),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button type="text" icon={<MoreOutlined />} />
                  </Dropdown>
                );
              },
            },
          ]}
        />
      </TableContainer>
    </div>
  );
};

export default LocationsSection;
